import { batch } from "solid-js";
import { reconcile } from "solid-js/store";

import Promotion, {
  PromotionCanada,
  PromotionUsa,
} from "@repo/kikoff-models/Promotion";
import Subscription from "@repo/kikoff-models/Subscription";
import canadaRpc from "@repo/protobuf/api/canadaRpc";
import usRpc from "@repo/protobuf/api/usRpc";
import { User as UsUserPb } from "@repo/protobuf/gen/web/public/user_pb";
import UBoolean from "@repo/utils/UBoolean";
import UObject from "@repo/utils/UObject";
import UPromise from "@repo/utils/UPromise";
import FeatureStore from "@repo/utils-solid/FeatureStore";

const initialState = {
  byId: {} as Record<Promotion.Id, Promotion>,
  signup: null as null | Promotion.Id,
  eligibleFor: null as Partial<
    Record<Subscription.Tier, Promotion.Id[]>
  > | null,
  appliedFor: null as Partial<Record<Subscription.Tier, Promotion.Id[]>> | null,
};

export const { Context, Provider, usePromotions } = FeatureStore.init(
  "Promotions",
  initialState,
  ([store, setStore], { attachAction, wrapActions }) => {
    const accessors = {
      signup: () => store.byId[store.signup!],
      eligibleFor: attachAction(
        (tier: Subscription.Tier) =>
          store.eligibleFor &&
          (store.eligibleFor[tier] || []).map((id) => store.byId[id]!),
        () => actions.fetchEligible()
      ),
      appliedFor: attachAction(
        (tier: Subscription.Tier | undefined) =>
          tier &&
          store.appliedFor &&
          (store.appliedFor[tier] || []).map((id) => store.byId[id]!),
        () => actions.fetchApplied()
      ),
      query: (
        type: "eligible" | "applied",
        query: UObject.DeepPartial<Promotion>
      ) =>
        Object.values(store[`${type}For`] || ([] as never))
          .filter(UBoolean.isTruthy)
          .flat()
          .find((promoId) =>
            UObject.matchesPartial.deep(store.byId[promoId]!, query)
          ),
    };

    const mutations = {
      one(promo: Promotion) {
        setStore("byId", promo.id, reconcile(promo));
      },
      many(promos: Promotion[]) {
        batch(() => {
          for (const promo of promos) {
            mutations.one(promo);
          }
        });
      },
    };

    const usa = {
      actions: {
        fetchSignupPromo() {
          let attempts = 5;
          //  User leads are populated asynchronously in a job in the backend,
          //  we must poll to ensure intro offer uses up to date user leads.
          //  Signup promos depend on user leads.
          return (function poll() {
            return usRpc.Promotions.exposeIntroTestOffer({}).then(
              async ({ promo }) => {
                if (!promo) {
                  if (--attempts <= 0) return;

                  await UPromise.delay(1000);
                  return (poll as any)() as never;
                }
                const normalized = PromotionUsa.normalize(promo);
                batch(() => {
                  mutations.one(normalized);
                  setStore("signup", normalized.id);
                });

                return normalized;
              }
            );
          })();
        },
        fetchEligible() {
          return usRpc.Account.getUserInfo({
            scopes: [UsUserPb.Scope.FEATURES],
          }).then(({ user }) => {
            const normalized = user!.promos.map(PromotionUsa.normalize);
            batch(() => {
              mutations.many(normalized);
              setStore(
                "eligibleFor",
                UObject.groupByMultipleEntries(normalized, (p) => [
                  [p.validTiers, p.id],
                ])
              );
            });
          });
        },
        fetchApplied() {},
      },
    };
    const canada = {
      actions: {
        fetchSignupPromo() {
          return Promise.resolve();
        },
        fetchEligible() {
          // Currently we only have intro promos which are immediately applied
          // before credit line opens--this behaves differently from the US api.
          // If we later want to add lifecycle eligibility, we will need to
          // include intro offers in the fetch eligible response even though
          // they're eagerly applied.
          return canada.actions.fetchApplied();
        },
        fetchApplied() {
          return canadaRpc.Promotion.userPromotion({}).then(({ promotion }) => {
            if (!promotion) return;
            const normalized = PromotionCanada.normalize(promotion);
            batch(() => {
              mutations.one(normalized);
              setStore(
                ["eligibleFor", "appliedFor"],
                UObject.from.keys(normalized.validTiers, [normalized.id])
              );
            });
          });
        },
      },
    };

    const actions = wrapActions(
      import.meta.env.API_ADAPTER === "canada" ? canada.actions : usa.actions
    );

    return { accessors, actions };
  }
);
